import React, { useState, useCallback } from 'react';
import ElementorToolbar from './ElementorToolbar';
import ElementorSidebar from './ElementorSidebar';
import ElementorCanvas from './ElementorCanvas';
import ElementorSettings from './ElementorSettings';
import PreviewModal from './PreviewModal';

const PageBuilder = ({ page, onSave }) => {
  const [sections, setSections] = useState(page.content || []);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [pageSettings, setPageSettings] = useState(page.settings || {});
  const [deviceView, setDeviceView] = useState('desktop');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showPreview, setShowPreview] = useState(false);

  // Save to history
  const saveToHistory = useCallback((newSections) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newSections)));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  }, [history, historyIndex]);

  const updateSections = useCallback((newSections, recordHistory = true) => {
    setSections(newSections);
    if (recordHistory) {
      saveToHistory(newSections);
    }
  }, [saveToHistory]);

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setSections(JSON.parse(JSON.stringify(history[historyIndex - 1])));
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setSections(JSON.parse(JSON.stringify(history[historyIndex + 1])));
    }
  };

  const addSection = (sectionType) => {
    const newSection = {
      id: `section_${Date.now()}`,
      type: sectionType,
      settings: {
        backgroundColor: '#ffffff',
        padding: { top: 50, right: 0, bottom: 50, left: 0 },
        margin: { top: 0, right: 0, bottom: 0, left: 0 },
        borderRadius: 0,
        boxShadow: 'none',
        backgroundImage: null,
        backgroundAttachment: 'scroll',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover'
      },
      columns: getDefaultColumns(sectionType)
    };

    updateSections([...sections, newSection]);
  };

  const getDefaultColumns = (sectionType) => {
    const timestamp = Date.now();

    switch (sectionType) {
      case 'one-column':
        return [{
          id: `col_${timestamp}`,
          width: 100,
          widgets: [],
          settings: {
            padding: { top: 20, right: 20, bottom: 20, left: 20 },
            backgroundColor: 'transparent',
            borderRadius: 0
          }
        }];

      case 'two-column':
        return [
          { id: `col_${timestamp}_1`, width: 50, widgets: [], settings: {} },
          { id: `col_${timestamp}_2`, width: 50, widgets: [], settings: {} }
        ];

      case 'three-column':
        return [
          { id: `col_${timestamp}_1`, width: 33.33, widgets: [], settings: {} },
          { id: `col_${timestamp}_2`, width: 33.33, widgets: [], settings: {} },
          { id: `col_${timestamp}_3`, width: 33.33, widgets: [], settings: {} }
        ];

      case 'two-column-unequal':
        return [
          { id: `col_${timestamp}_1`, width: 33.33, widgets: [], settings: {} },
          { id: `col_${timestamp}_2`, width: 66.67, widgets: [], settings: {} }
        ];

      case 'two-column-unequal-reverse':
        return [
          { id: `col_${timestamp}_1`, width: 66.67, widgets: [], settings: {} },
          { id: `col_${timestamp}_2`, width: 33.33, widgets: [], settings: {} }
        ];

      case 'four-column':
        return [
          { id: `col_${timestamp}_1`, width: 25, widgets: [], settings: {} },
          { id: `col_${timestamp}_2`, width: 25, widgets: [], settings: {} },
          { id: `col_${timestamp}_3`, width: 25, widgets: [], settings: {} },
          { id: `col_${timestamp}_4`, width: 25, widgets: [], settings: {} }
        ];

      default:
        return [];
    }
  };

  const updateSection = (sectionId, updates) => {
    const updatedSections = sections.map(section =>
      section.id === sectionId ? { ...section, ...updates } : section
    );
    updateSections(updatedSections);
    
    if (selectedSection?.id === sectionId) {
      const updatedSection = updatedSections.find(section => section.id === sectionId);
      setSelectedSection(updatedSection);
    }
  };

  const deleteSection = (sectionId) => {
    const updatedSections = sections.filter(section => section.id !== sectionId);
    updateSections(updatedSections);
    if (selectedSection?.id === sectionId) {
      setSelectedSection(null);
      setShowSettings(false);
    }
  };

  const duplicateSection = (section) => {
    const newSection = {
      ...section,
      id: `section_${Date.now()}`,
      columns: section.columns.map(col => ({
        ...col,
        id: `col_${Date.now()}_${Math.random()}`,
        widgets: col.widgets.map(widget => ({
          ...widget,
          id: `widget_${Date.now()}_${Math.random()}`
        }))
      }))
    };
    const index = sections.findIndex(s => s.id === section.id);
    const newSections = [...sections];
    newSections.splice(index + 1, 0, newSection);
    updateSections(newSections);
  };

  const addWidget = (sectionId, columnId, widgetType) => {
    const newWidget = {
      id: `widget_${Date.now()}`,
      type: widgetType,
      content: getDefaultWidgetContent(widgetType),
      settings: getDefaultWidgetSettings(widgetType),
      styles: getDefaultWidgetStyles(widgetType)
    };

    const updatedSections = sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          columns: section.columns.map(col => {
            if (col.id === columnId) {
              return {
                ...col,
                widgets: [...col.widgets, newWidget]
              };
            }
            return col;
          })
        };
      }
      return section;
    });

    updateSections(updatedSections);
  };

  const getDefaultWidgetContent = (type) => {
    switch (type) {
      // Basic Elements
      case 'heading':
        return { text: 'Add Your Heading Text Here', tag: 'h2' };
      case 'text':
        return { text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut elit tellus, luctus nec ullamcorper mattis, pulvinar dapibus leo.' };
      case 'paragraph':
        return { text: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.' };
      case 'button':
        return { text: 'Click Here', link: '#', icon: null, variant: 'primary' };
      case 'link':
        return { text: 'Click Here', url: '#', target: '_self' };
      case 'icon':
        return { name: 'star', size: 48, color: '#007bff' };
      case 'quote':
        return { text: 'This is an inspiring quote that will capture your audience\'s attention.', author: 'John Doe' };
      case 'list':
        return { items: ['Item 1', 'Item 2', 'Item 3'], type: 'unordered' };

      // Media Elements
      case 'image':
        return { url: 'https://via.placeholder.com/800x400', alt: 'Image', caption: '' };
      case 'video':
        return { url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', autoplay: false, controls: true };
      case 'audio':
        return { url: '', autoplay: false, controls: true, loop: false };
      case 'gallery':
        return { images: [], columns: 3, spacing: 10 };
      case 'slider':
        return { images: [], autoplay: true, interval: 3000, showDots: true, showArrows: true };
      case 'map':
        return { address: 'New York, NY', zoom: 12, height: 400 };

      // Layout Elements
      case 'divider':
        return { style: 'solid', weight: 2, color: '#ddd', width: 100 };
      case 'spacer':
        return { height: 50 };
      case 'accordion':
        return { items: [{ title: 'Section 1', content: 'Content for section 1' }, { title: 'Section 2', content: 'Content for section 2' }] };
      case 'tabs':
        return { tabs: [{ title: 'Tab 1', content: 'Content for tab 1' }, { title: 'Tab 2', content: 'Content for tab 2' }], activeTab: 0 };

      // Dynamic Elements
      case 'counter':
        return { number: 100, suffix: '+', prefix: '', duration: 2 };
      case 'progress-bar':
        return { percentage: 75, title: 'Progress', color: '#007bff', showLabel: true };
      case 'form':
        return { fields: [], method: 'POST', action: '#', submitText: 'Submit' };
      case 'social-share':
        return { platforms: ['facebook', 'twitter', 'linkedin'], showCount: false };
      case 'social-icons':
        return { icons: ['facebook', 'twitter', 'instagram', 'linkedin'], size: 24, color: '#333' };

      // Form Elements
      case 'input-text':
        return { label: 'Text Input', placeholder: 'Enter text...', required: false, value: '' };
      case 'input-email':
        return { label: 'Email Address', placeholder: 'you@example.com', required: false, value: '' };
      case 'input-password':
        return { label: 'Password', placeholder: 'Enter password...', required: false, value: '' };
      case 'input-number':
        return { label: 'Number', placeholder: 'Enter number...', min: 0, max: 100, step: 1, value: 0 };
      case 'input-date':
        return { label: 'Date', placeholder: 'Select date...', format: 'YYYY-MM-DD', value: '' };
      case 'input-time':
        return { label: 'Time', placeholder: 'Select time...', format: 'HH:mm', value: '' };
      case 'textarea':
        return { label: 'Message', placeholder: 'Enter your message...', rows: 4, value: '' };
      case 'select':
        return { label: 'Select Option', options: ['Option 1', 'Option 2', 'Option 3'], value: '' };
      case 'checkbox':
        return { label: 'Checkbox', options: [{ label: 'Option 1', value: 'option1', checked: false }] };
      case 'radio':
        return { label: 'Radio Group', name: 'radio-group', options: [{ label: 'Option 1', value: 'option1', checked: true }] };
      case 'toggle':
        return { label: 'Toggle Switch', checked: false, onText: 'On', offText: 'Off' };
      case 'range':
        return { label: 'Range Slider', min: 0, max: 100, value: 50, step: 1 };
      case 'color-picker':
        return { label: 'Color', value: '#007bff' };
      case 'input-file':
        return { label: 'File Upload', accept: 'image/*', multiple: false, value: null };

      // Advanced Elements
      case 'modal':
        return { title: 'Modal Title', content: 'Modal content goes here...', triggerText: 'Open Modal', closeOnBackdrop: true };
      case 'tooltip':
        return { text: 'Tooltip Text', position: 'top', trigger: 'hover' };
      case 'alert':
        return { message: 'This is an alert message!', type: 'info', dismissible: true };
      case 'badge':
        return { text: 'New', color: '#007bff', variant: 'rounded' };
      case 'code':
        return { code: '<div>Your code here</div>', language: 'html' };
      case 'embed':
        return { url: '', html: '', width: '100%', height: 400 };

      // E-commerce Elements
      case 'product':
        return { name: 'Product Name', price: 29.99, image: 'https://via.placeholder.com/300x300', description: 'Product description here...', addToCartText: 'Add to Cart' };
      case 'product-grid':
        return { products: [], columns: 3, showPrice: true, showAddToCart: true };

      default:
        return {};
    }
  };

  const getDefaultWidgetSettings = (type) => {
    return {
      animation: 'none',
      animationDuration: 1000,
      animationDelay: 0,
      visibility: 'visible',
      zIndex: 1,
      cssClasses: '',
      cssId: ''
    };
  };

  const getDefaultWidgetStyles = (type) => {
    return {
      margin: { top: 0, right: 0, bottom: 0, left: 0 },
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
      textAlign: 'left',
      color: '#333333',
      backgroundColor: 'transparent',
      fontSize: 16,
      fontWeight: 'normal',
      lineHeight: 1.5,
      borderRadius: 0
    };
  };

  const updateWidget = (sectionIdOrWidgetId, columnIdOrUpdates, widgetIdOrUndefined, updatesOrUndefined) => {
    let sectionId = sectionIdOrWidgetId;
    let columnId = columnIdOrUpdates;
    let widgetId = widgetIdOrUndefined;
    let updates = updatesOrUndefined;

    if (typeof columnIdOrUpdates === 'object' && widgetIdOrUndefined === undefined) {
      updates = columnIdOrUpdates;
      widgetId = sectionIdOrWidgetId;
      sectionId = selectedElement?.sectionId;
      columnId = selectedElement?.columnId;
    }

    if (!sectionId || !columnId || !widgetId || !updates) return;

    const updatedSections = sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          columns: section.columns.map(col => {
            if (col.id === columnId) {
              return {
                ...col,
                widgets: col.widgets.map(widget =>
                  widget.id === widgetId ? { ...widget, ...updates } : widget
                )
              };
            }
            return col;
          })
        };
      }
      return section;
    });

    updateSections(updatedSections);

    if (selectedElement?.id === widgetId && selectedElement?.sectionId === sectionId && selectedElement?.columnId === columnId) {
      const updatedWidget = updatedSections
        .find(section => section.id === sectionId)
        ?.columns.find(col => col.id === columnId)
        ?.widgets.find(widget => widget.id === widgetId);

      if (updatedWidget) {
        setSelectedElement({ ...updatedWidget, sectionId, columnId });
      }
    }
  };

  const deleteWidget = (sectionId, columnId, widgetId) => {
    const updatedSections = sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          columns: section.columns.map(col => {
            if (col.id === columnId) {
              return {
                ...col,
                widgets: col.widgets.filter(widget => widget.id !== widgetId)
              };
            }
            return col;
          })
        };
      }
      return section;
    });

    updateSections(updatedSections);
  };

  const moveWidget = (widgetId, sourceSectionId, sourceColumnId, targetSectionId, targetColumnId) => {
    if (!widgetId || !sourceSectionId || !sourceColumnId || !targetSectionId || !targetColumnId) return;

    let movedWidget = null;
    const sectionsWithoutWidget = sections.map(section => {
      if (section.id !== sourceSectionId) return section;

      return {
        ...section,
        columns: section.columns.map(col => {
          if (col.id !== sourceColumnId) return col;
          const widgetIndex = col.widgets.findIndex(widget => widget.id === widgetId);
          if (widgetIndex === -1) return col;
          movedWidget = col.widgets[widgetIndex];
          return {
            ...col,
            widgets: col.widgets.filter(widget => widget.id !== widgetId)
          };
        })
      };
    });

    if (!movedWidget) return;

    const updatedSections = sectionsWithoutWidget.map(section => {
      if (section.id !== targetSectionId) return section;

      return {
        ...section,
        columns: section.columns.map(col => {
          if (col.id !== targetColumnId) return col;
          return {
            ...col,
            widgets: [...col.widgets, movedWidget]
          };
        })
      };
    });

    updateSections(updatedSections);
  };

  const moveSection = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    
    const newSections = [...sections];
    const [movedSection] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, movedSection);
    
    updateSections(newSections);
  };

  const savePage = () => {
    const updatedPage = {
      ...page,
      content: sections,
      settings: pageSettings,
      lastModified: new Date().toISOString(),
      title: pageSettings.pageTitle || page.title
    };
    onSave(updatedPage);
  };

  const handlePreview = () => {
    setShowPreview(true);
  };

  const handleExportJSON = () => {
    const formData = {
      id: page.id,
      title: page.title || pageSettings.pageTitle,
      content: sections,
      settings: pageSettings,
      lastModified: new Date().toISOString(),
    };

    const dataStr = JSON.stringify(formData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${page.title || 'form'}_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <ElementorToolbar
        onUndo={undo}
        onRedo={redo}
        onSave={savePage}
        onPreview={handlePreview}
        onExportJSON={handleExportJSON}
        deviceView={deviceView}
        setDeviceView={setDeviceView}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
      />

      <div className="builder-main">
        <ElementorSidebar
          onAddSection={addSection}
          onAddWidget={addWidget}
          selectedSection={selectedSection}
          selectedElement={selectedElement}
        />

        <ElementorCanvas
          sections={sections}
          selectedSection={selectedSection}
          selectedElement={selectedElement}
          setSelectedSection={setSelectedSection}
          setSelectedElement={setSelectedElement}
          setShowSettings={setShowSettings}
          onUpdateSection={updateSection}
          onDeleteSection={deleteSection}
          onDuplicateSection={duplicateSection}
          onAddWidget={addWidget}
          onUpdateWidget={updateWidget}
          onDeleteWidget={deleteWidget}
          onMoveWidget={moveWidget}
          onMoveSection={moveSection}
          onOpenWidgetSettings={(widget) => {
            setSelectedElement(widget);
            setShowSettings(true);
          }}
          deviceView={deviceView}
        />

        <ElementorSettings
          selectedSection={selectedSection}
          selectedElement={selectedElement}
          sections={sections}
          onUpdateSection={updateSection}
          onUpdateWidget={updateWidget}
          pageSettings={pageSettings}
          setPageSettings={setPageSettings}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
        />
      </div>

      <PreviewModal
        page={page}
        sections={sections}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        onExportJSON={handleExportJSON}
      />
    </>
  );
};

export default PageBuilder;